import { Fragment } from 'react';
import { cn } from '@rocco/utils/cn';

/** How far apart consecutive words start rising, in milliseconds. */
const WORD_STAGGER_MS = 45;

interface RevampTitleRevealProps {
    text: string;
    className?: string;
}

/**
 * The headline rising into place, a word at a time.
 *
 * Each word sits in a clipping box of its own and starts below it, so it comes
 * up out of nothing rather than fading in; the words start a beat apart, so a
 * two-line headline reads as its lines rising in turn. Plain CSS, so it plays
 * from the first paint on a full load as well as on a client-side arrival, and
 * it is skipped for a reader who asked for reduced motion.
 *
 * The boxes are inline blocks, so the line wrapping is the browser's own and
 * a long headline still breaks where it would have.
 */
export const RevampTitleReveal = ({
    text,
    className,
}: RevampTitleRevealProps) => (
    <span className={className}>
        {text.split(/\s+/).map((word, index) => (
            // Words repeat within a headline, so the position is the key.
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>
                {/* The space sits between the boxes, not inside one: a box's
                    trailing space is collapsed away, and the words ran
                    together. */}
                {index > 0 ? ' ' : null}
                <span
                    // The clip box takes the descenders' room below the line
                    // and gives it back with the margin, so a g or a p is
                    // not cut at the baseline and the lines keep their pitch.
                    className="-mb-[0.22em] inline-block overflow-hidden pb-[0.22em] align-top"
                >
                    <span
                        style={{
                            animationDelay: `${index * WORD_STAGGER_MS}ms`,
                        }}
                        className={cn(
                            'inline-block',
                            'animate-[title-rise_1.1s_cubic-bezier(0.16,1,0.3,1)_both]',
                            'motion-reduce:animate-none',
                        )}
                    >
                        {word}
                    </span>
                </span>
            </Fragment>
        ))}
    </span>
);
